import ModalResponsive, { ModalResponsiveFooter } from '@/components/commons/modal-responsive';
import { SelectSearch } from '@/components/commons/select-search';
import MapService from '@/components/maps-service';
import FieldErrorMessage from '@/components/sections/field-error-message';
import { Button } from '@/components/ui/button';
import { FormControl, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { useAreas } from '@/hooks/api/storefront/orders';
import { useDebounceValue } from '@/hooks/use-debounce';
import { createCustomerAddress, updateCustomerAddress } from '@/lib/actions/customer/action';
import { queryClient } from '@/lib/query-client';
import { StoreCustomerAddressPayload } from '@/types/http';
import { Form, Formik } from 'formik';
import { MapPinIcon } from 'lucide-react';
import React from 'react';
import * as Yup from 'yup';

const customerAddressSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required').min(2).max(50),
  last_name: Yup.string().optional().min(2).max(50),
  phone: Yup.string()
    .matches(/^62\d+$/, 'Phone numbers must begin with 62')
    .max(20, 'Phone number cannot be longer than 15 characters')
    .required('Phone number is required'),
  area: Yup.string().required('Area is required'),
  address: Yup.string().required(),
});

type ModalCustomerAddressProps = {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit?: (data: StoreCustomerAddressPayload) => void;
  initialValues?: StoreCustomerAddressPayload;
  includeEmail?: boolean;
  mode?: 'add' | 'edit';
  host?: string;
  token?: string;
};

const useCustomerAddress = (mode: 'add' | 'edit', inititalValues?: StoreCustomerAddressPayload) => {
  const [defaultValues, setDefaultValues] = React.useState<StoreCustomerAddressPayload>({
    first_name: '',
    last_name: '',
    phone: '',
    area: '',
    area_name: '',
    address: '',
    zipcode: '',
    pinpoint: { lat: '', long: '' },
  });

  React.useEffect(() => {
    if (mode === 'edit' && inititalValues) {
      setDefaultValues({
        ...inititalValues,
        area_name: inititalValues.area_name || '',
        last_name: inititalValues.last_name || '',
        pinpoint:
          typeof inititalValues.pinpoint === 'string'
            ? JSON.parse(inititalValues.pinpoint)
            : { lat: '', long: '' },
      });
    } else {
      setDefaultValues({
        first_name: '',
        last_name: '',
        phone: '',
        area: '',
        area_name: '',
        address: '',
        zipcode: '',
        pinpoint: { lat: '', long: '' },
      });
    }
  }, [inititalValues]);

  return defaultValues;
};

export const ModalCustomerAddress: React.FC<ModalCustomerAddressProps> = ({
  openModal,
  setOpenModal,
  initialValues,
  mode = 'add',
  host,
  token,
}) => {
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [isShowMap, setIsShowMap] = React.useState<boolean>(false);

  const debouncedSearchValue = useDebounceValue(searchValue, 500);
  const defaultValues = useCustomerAddress(mode as 'add' | 'edit', initialValues);

  const { data: areas } = useAreas(
    { q: debouncedSearchValue },
    {
      enabled: !!debouncedSearchValue,
    }
  );
  const areaOptions =
    areas?.map((item) => ({
      label: item.name,
      value: item.area_id,
    })) || [];

  const handleRequest = async (
    action: 'create' | 'update',
    values: StoreCustomerAddressPayload,
    id?: string
  ) => {
    try {
      const payload = {
        ...values,
        pinpoint: typeof values.pinpoint === 'string' ? JSON.parse(values.pinpoint) : null,
      };
      const res =
        action === 'create'
          ? await createCustomerAddress(host as string, token as string, payload)
          : await updateCustomerAddress(host as string, token as string, id as string, payload);

      if (res.success) {
        queryClient.invalidateQueries({ queryKey: ['customer_list_address'] });
        toast({
          title: 'Success',
          variant: 'success',
          description: res.message || `Address ${action}d successfully`,
        });
        setIsShowMap(false);
      } else {
        toast({
          title: 'Error',
          variant: 'destructive',
          description: res.message || 'Failed to create address',
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Error',
          variant: 'destructive',
          description: error.message || 'Something went wrong',
        });
      }
    } finally {
      setOpenModal(false);
    }
  };

  const handleOnSubmit = (values: StoreCustomerAddressPayload) => {
    handleRequest(mode === 'add' ? 'create' : 'update', values, initialValues?.id);
  };

  const handleOnCloseModal = () => {
    setIsShowMap(false);
    setOpenModal(false);
  };

  return (
    <ModalResponsive
      title='Customer Address'
      openModal={openModal}
      setOpenModal={handleOnCloseModal}
      className='theme_font max-w-3xl'
    >
      <Formik
        initialValues={defaultValues}
        validationSchema={customerAddressSchema}
        onSubmit={handleOnSubmit}
      >
        {({ handleSubmit, handleChange, handleBlur, values, errors, setFieldValue }) => (
          <>
            <Form
              onSubmit={handleSubmit}
              className='max-h-[420px] space-y-5 overflow-y-auto px-6 py-2'
            >
              <div className='grid grid-cols-2 gap-4'>
                <FormItem className='text-sm'>
                  <FormControl>
                    <Input
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.first_name as string}
                      name='first_name'
                      type='text'
                      size={100}
                      placeholder='First name'
                    />
                  </FormControl>
                  <FieldErrorMessage error={errors.first_name} />
                </FormItem>
                <FormItem className='text-sm'>
                  <FormControl>
                    <Input
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name='last_name'
                      value={values.last_name}
                      type='text'
                      size={100}
                      placeholder='Last name'
                    />
                  </FormControl>
                </FormItem>
              </div>
              <FormItem className='text-sm'>
                <FormControl>
                  <Input
                    name='phone'
                    value={values.phone}
                    type='text'
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.match(/^[0-9]*$/)) {
                        handleChange(e);
                      }
                    }}
                    placeholder='Phone number'
                  />
                </FormControl>
                <FieldErrorMessage error={errors.phone} />
              </FormItem>
              <FormItem className='text-sm'>
                <FormControl>
                  <Textarea
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.address}
                    name='address'
                    placeholder='Detail address'
                    rows={3}
                  />
                </FormControl>
              </FormItem>
              <FormItem className='text-sm'>
                <FormControl>
                  <SelectSearch
                    options={areaOptions}
                    isCommandInput
                    placeholder='Search area'
                    emptyMessage='No Area Found'
                    onSelect={(value) => {
                      const selectedArea = areas?.find((item) => item.area_id === value.value);
                      setFieldValue('area', selectedArea?.area_id);
                      setFieldValue('area_name', selectedArea?.name);
                      setFieldValue('zipcode', selectedArea?.zipcode);
                    }}
                    onInputChange={setSearchValue}
                    defaultInputValue={values.area_name}
                    isLoading={false}
                  />
                </FormControl>
                <FieldErrorMessage error={errors.area} />
              </FormItem>
              <FormItem className='mb-4 text-sm'>
                <div className='flex items-center justify-between pb-3'>
                  <div className='flex flex-col gap-2'>
                    <Label htmlFor='customer.pinpoint' className='!-mb-1 flex gap-x-1 font-normal'>
                      Pinpoint location <span className='text-muted-foreground'>( Optional )</span>
                    </Label>
                    <span className='text-muted-foreground'>Required for Gojek/Grab</span>
                  </div>
                  <button
                    type='button'
                    onClick={() => setIsShowMap(!isShowMap)}
                    className='flex h-8 items-center text-primary underline'
                  >
                    <MapPinIcon className='mr-2 h-4 w-4' />
                    pinpoint on map
                  </button>
                </div>
                {/* Keep map outside of form render cycle */}
                {isShowMap && (
                  <MapService
                    onResultUpdate={(result) => {
                      setFieldValue(
                        'pinpoint',
                        JSON.stringify({
                          lat: result.coordinates.lat,
                          long: result.coordinates.lng,
                        })
                      );
                    }}
                    coordinates={{
                      lat: Number(defaultValues.pinpoint.lat) || -6.216601629606373,
                      lng: Number(defaultValues.pinpoint.long) || 106.8182250518579,
                    }}
                  />
                )}
              </FormItem>
            </Form>
            <ModalResponsiveFooter>
              <Button
                onClick={() => setOpenModal?.(false)}
                type='button'
                variant='outline'
                className='hidden md:flex'
              >
                Cancel
              </Button>
              <Button type='button' onClick={() => handleSubmit()}>
                Save
              </Button>
            </ModalResponsiveFooter>
          </>
        )}
      </Formik>
    </ModalResponsive>
  );
};
